import { useState, useEffect, useMemo } from 'react'
import { useSessionStore } from '../../lib/Store/sessionStore.component'
import { FicheSalaireApi } from './FicheSalaire.actions'
import { usePolling } from '../../hooks/usePolling/usePolling.hook'
import { useNotify } from '../../hooks/useNotify/useNotify'

import { BulletinPaie } from './FicheSalaire.types'
import { Dayjs } from 'dayjs'

export const useFicheSalaireState = (currentDate: Dayjs) => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();
  const notify = useNotify();

  const [selectedBulletin, setSelectedBulletin] = useState<BulletinPaie | null>(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [bulletinsPaie, setBulletinsPaie] = useState<BulletinPaie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfKey, setPdfKey] = useState<number>(0); // Clé pour forcer le rechargement du PdfViewer
  const currentYear = useMemo(() => currentDate.year(), [currentDate])

  // Chargement des bulletins de paie depuis l'API
  useEffect(() => {
    if (!currentLogin?.identifiant) return

    const loadBulletins = async () => {

      try {
        setLoading(true)
        setError(null)

        const bulletins = await FicheSalaireApi.loadBulletinsPaie(
          currentLogin?.identifiantEntiteDefautEsag || 0,
          currentLogin?.identifiant.toString() || '',
          currentYear
        )

        setBulletinsPaie(bulletins)
      } catch (err) {
        console.error('❌ Erreur lors du chargement des bulletins:', err)
        setError('Erreur lors du chargement des bulletins de paie')
      } finally {
        setLoading(false)
      }
    }

    loadBulletins()
  }, [currentLogin?.identifiant, currentLogin?.identifiantEntiteDefautEsag, currentYear])

  // Polling silencieux pour détecter de nouveaux bulletins
  usePolling<BulletinPaie[]>({
    fetchFn: async () => {
      if (!currentLogin?.identifiant) return null
      return FicheSalaireApi.loadBulletinsPaie(
        currentLogin.identifiantEntiteDefautEsag || 0,
        currentLogin.identifiant.toString(),
        currentYear,
        true
      )
    },
    enabled: !!currentLogin?.identifiant,
    hasChanged: (prev, next) => {
      if (prev.length !== next.length) return true
      const prevIds = prev.map(b => b.id).sort()
      const nextIds = next.map(b => b.id).sort()
      return prevIds.some((id, i) => id !== nextIds[i])
    },
    onChanged: (next) => {
      setBulletinsPaie(next)
      notify.info('Vos bulletins de paie ont été mis à jour', 3000)
    },
  })

  // Actions
  const handleBulletinPress = async (bulletin: BulletinPaie) => {
    try {
      // Télécharger le document et mettre à jour l'URI si nécessaire
      const updatedBulletin = await FicheSalaireApi.downloadBulletin(bulletin);

      // Incrémenter la clé pour forcer le rechargement du PdfViewer
      setPdfKey(prev => prev + 1);

      setSelectedBulletin(updatedBulletin);
      setShowPdfViewer(true);

      // Mettre à jour le bulletin dans la liste
      setBulletinsPaie(prev =>
        prev.map(b => b.id === updatedBulletin.id ? updatedBulletin : b)
      );
    } catch (downloadError) {
      console.error('❌ Erreur lors de l\'ouverture du bulletin:', downloadError);
      // En cas d'erreur, afficher quand même le bulletin sans le PDF
      setPdfKey(prev => prev + 1);
      setSelectedBulletin(bulletin);
      setShowPdfViewer(true);
    }
  };

  const closePdfViewer = () => {
    setShowPdfViewer(false);
    setSelectedBulletin(null);
  };

  return {
    // États
    selectedBulletin,
    showPdfViewer,
    bulletinsPaie,
    loading,
    error,
    pdfKey,

    // Actions
    handleBulletinPress,
    closePdfViewer,
  }
}
